import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import Post from './Post'
import Author from './Author'
import Pagination from '../Pagination'
import { blogAuthorPropType, blogPostPropType } from '../../constants/propTypes'
import { getURIPathWithoutLang } from '../../helpers/url'
import { LanguageConsumer } from '../../state'
import { FaArrowCircleUp } from 'react-icons/fa'
import Link from '../Link'
import Query from './Query'
import { scrollTo } from '../../helpers/dom'
import { Location } from '@reach/router'

const Container = styled.div`
  display: inline-block;
  width: 70%;
  vertical-align: top;
  position: relative;
  padding-right: 2rem;

  @media screen and (max-width: 1000px) {
    display: block;
    width: 100%;
    padding-right: 0;
  }
`

const Items = styled.div`
  margin: 3rem 0;
  display: ${({ isInline }) => (isInline ? 'inline-block' : 'block')};

  > div {
    display: ${({ isInline }) => (isInline ? 'inherit' : 'block')};
    width: ${({ isInline }) => (isInline ? '25%' : '100%')};
    margin: ${({ isInline }) => (isInline ? 2 : 8)}rem 0;
    vertical-align: top;

    &:first-of-type {
      margin-top: ${({ isInline }) => (isInline ? 2 : 0)}rem;
    }

    &:last-of-type {
      margin-bottom: ${({ isInline }) => (isInline ? 2 : 0)}rem;
    }

    @media screen and (max-width: 900px) {
      width: ${({ isInline }) => (isInline ? '33%' : '100%')};
    }

    @media screen and (max-width: 700px) {
      width: ${({ isInline }) => (isInline ? '50%' : '100%')};
    }

    @media screen and (max-width: 500px) {
      width: 100%;
    }
  }
`

const BackToTop = styled.div`
  margin: 1rem 0;
  text-align: center;
  font-size: 2.4rem;

  svg {
    display: block;
    margin: 1rem auto;
  }
`

const PaginatedItems = (props) => {
  const items = props.posts || props.authors
  const getPathFromPage = (page, currentPath) => {
    currentPath = currentPath.replace(/^\//, '').replace(/\/$/, '')
    const parts = currentPath.split('/')
    const pagePart = parts[parts.length - 1]
    if (pagePart.match(/page-[\d]+/))
      return `/${parts.slice(0, parts.length - 1).join('/')}/page-${page}/`
    return `/${parts.join('/')}/page-${page}/`
  }

  const scrollToTop = (e) => {
    e.preventDefault()
    scrollTo({ x: 0, y: 0 })
  }

  return (
    <Query
      render={(content) => (
        <LanguageConsumer>
          {({ lang }) => (
            <Container>
              <Items isInline={props.authors}>
                {items.map((item) => (
                  <Fragment key={item.slug || item.profile_url}>
                    {props.posts && (
                      <div>
                        <Post {...item} lang={lang} />
                      </div>
                    )}
                    {props.authors && (
                      <div>
                        <Author {...item} />
                      </div>
                    )}
                  </Fragment>
                ))}
              </Items>
              <Location>
                {({ location }) => (
                  <Pagination
                    currentPage={props.currentPage}
                    totalPages={props.totalPages}
                    getHref={(page) =>
                      `${getPathFromPage(
                        page,
                        getURIPathWithoutLang(lang, location.pathname)
                      )}#${
                        props.posts
                          ? 'blog-posts'
                          : props.authors
                          ? 'authors'
                          : ''
                      }`
                    }
                  />
                )}
              </Location>
              <BackToTop>
                <Link
                  href="#"
                  tracking={{ label: 'blog_page_back_to_top' }}
                  onClick={scrollToTop}
                >
                  <FaArrowCircleUp />
                  {content.back_to_top}
                </Link>
              </BackToTop>
            </Container>
          )}
        </LanguageConsumer>
      )}
    />
  )
}

PaginatedItems.propTypes = {
  posts: PropTypes.arrayOf(PropTypes.shape(blogPostPropType)),
  authors: PropTypes.arrayOf(PropTypes.shape(blogAuthorPropType)),
  totalPages: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired
}

export default PaginatedItems
